import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Button, Loader, SearchInput } from '../../components'
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime'
import { getOperatorUsers } from '../../services/userService'
import { useQuery } from 'react-query'
import { createPickingOrder, reassignPickingOrder } from '../../services'
import { MultiPickerAssignView } from './MultiPickerAssignDialog'
import { Trans, useTranslation } from 'react-i18next'
import { Pagination, Table, Tooltip } from 'antd'
import { ChevronDownIcon, ExclamationIcon, XIcon } from '@heroicons/react/solid'
import { groupOrdersColumns, pickerColumns } from './AssignPickingOrders/AssignOrdersColumnConfigs'
import { showErrorForColumnDirection } from './AssignPickingOrders/AssignOrdersGroupableIndicator'
import { PickingOrderErrorsPanel } from './AssignPickingOrders/PickingOrderErrorsPanel'
import { keepCompatibleSipKeys, sipToteBlocker } from './AssignPickingOrders/sipStoreBound'
import { PickabilityBreakdown } from './PickabilityBadge'
import { Link } from 'react-router-dom'
import { PICKING_TASKS_TO_BREAK } from '../../navigation/constants'

// The backend owns the verdict: NOTHING_PICKABLE is the only status it refuses to assign, and
// it means one thing — units locked in a box that cannot be taken whole. PARTIALLY_PICKABLE
// means missing units that picking does not require, so it stays assignable.
// The operators endpoint answers a throttled request with 202 and a message but no `users`.
// Failing the query lets react-query retry it instead of caching an empty picker list.
const fetchPickers = async params => {
  const res = await getOperatorUsers(params)
  if (!Array.isArray(res?.users)) throw new Error(res?.message || 'Operators request was not processed')
  return res
}

const isBlockedFromPicking = (e = {}) => e.pickability?.status === 'NOTHING_PICKABLE'

const OrderNumberList = ({ orders = [] }) => (
  <ul className="list-disc list-inside mt-1 font-mono font-semibold">
    {orders.map((o, i) => (
      <li key={o.entity?.id ?? i}>{o.entity?.order_number}</li>
    ))}
  </ul>
)

const ORDER_TYPE_PILL = {
  D2C: 'bg-blue-100 text-blue-800',
  B2B: 'bg-purple-100 text-purple-800',
  REM: 'bg-amber-100 text-amber-800',
}

// A "Rem" order: REMOVE_STOCK (and derivatives), pickup, or REM-coded order number.
const isRemEntity = (e = {}) =>
  (typeof e.order_type === 'string' && e.order_type.startsWith('REMOVE_STOCK')) ||
  e.shipping_method?.shipping_type === 'pickup' ||
  /REM/i.test(e.order_number || '')

// B2B and Rem orders must generate distributed picking tasks even when a single
// picker is assigned, so they go through the task (preview) flow regardless of
// how many pickers are selected.
const isB2bOrRemEntity = (e = {}) => !!e.is_b2b || isRemEntity(e)

// ...except when the order carries a unit-code tracked product (IMEI/serial). Only classic picking
// scans each unit against its reserved code and offers substitution; the task handheld has no such
// step, so a task-based pick lets the reservation drift from the physical serials. The backend
// applies the same rule on POST /picking-order, so distributing one here would silently end up on
// a single picker anyway.
const usesTaskFlowEntity = (e = {}) => isB2bOrRemEntity(e) && e.requires_unit_code !== true

export const AssignOrdersDialog = ({ open, orders = [], changingPicker = false, warehouseId, onSuccess = () => {}, onCancel = () => {} }) => {
  const { i18n } = useTranslation()
  // Every total below describes what will actually be assigned, not the raw selection.
  const assignableOrders = useMemo(() => (changingPicker ? orders : orders.filter(o => !isBlockedFromPicking(o.entity))), [orders, changingPicker])
  // Multi-picker (assigning several pickers at once) is only allowed for a single
  // order, and only for B2B/REM — those are the order types that use the picking-task
  // flow. D2C must never enter the task flow, so it stays single-picker (legacy).
  const multiPickerEnabled = !changingPicker && assignableOrders.length === 1 && usesTaskFlowEntity(assignableOrders[0]?.entity)

  const [searchParams, setSearchParams] = useState({
    search: '',
    page: 1,
    per_page: 5,
    warehouse_id: warehouseId,
  })

  useEffect(() => {
    setSearchParams(prev => ({ ...prev, warehouse_id: warehouseId }))
  }, [warehouseId])

  const {
    isLoading,
    isError,
    error,
    data: pickerRawData,
    isFetching,
    isPreviousData,
    refetch,
  } = useQuery(['operator_users', searchParams], () => fetchPickers(searchParams), { keepPreviousData: true, refetchOnWindowFocus: false, staleTime: 5000 })

  const [pickerData, setPickerData] = useState([])

  useEffect(() => {
    const pickerData = pickerRawData?.users?.map(picker => {
      // console.log("picker: ", picker)
      return {
        key: picker.id.toString(),
        ...picker,
      }
    })
    setPickerData(pickerData ?? [])
  }, [pickerRawData])

  // reset all when modal is opened
  useEffect(() => {
    if (open) {
      refetch()
      resetSelectedUser()
      const newOrdersTableData = mapOrdersAsTableData(orders)
      setActiveTab('select_picker')
      // Default reversed: nothing is grouped unless the user explicitly opts in.
      setTargetKeys([])
      setOrdersTableData(changingPicker ? newOrdersTableData : newOrdersTableData.filter(o => !isBlockedFromPicking(o)))
    }
  }, [open])

  const resetSelectedUser = () => {
    setSelectedUsers([])
    setSelectedUserKeys([])
  }

  const onSearchHandler = v => {
    setSearchParams({ ...searchParams, search: v, page: 1 })
  }

  const onClickCancel = () => {
    resetSelectedUser()
    setSearchParams({ ...searchParams, search: '', page: 1 })
    onCancel()
  }

  const [busy, setBusy] = useState(false)

  const onSubmit = async () => {
    setBusy(true)
    if (!changingPicker) {
      if (selectedUsers.length === 0 || ordersTableData.length < 1) {
        window.alert('missing data')
        setBusy(false)
        return
      }
      const firstPickerId = selectedUsers[0].id

      const ordersPayload = ordersTableData.map(order => {
        const forSip = targetKeys.findIndex(key => order.id.toString() === key) === -1 ? false : true
        return {
          id: order.id,
          single_item_picking: forSip,
        }
      })

      // Only B2B/REM orders use the picking-task (preview) flow; everything else
      // (D2C, and anything unit-code tracked) uses legacy createPickingOrder. This is the
      // authoritative gate — no such order can reach the task flow regardless of picker count.
      const allUseTaskFlow = ordersTableData.length > 0 && ordersTableData.every(usesTaskFlowEntity)
      const useTaskFlow = !changingPicker && allUseTaskFlow

      if (useTaskFlow) {
        // Task flow uses preview + atomic confirm. Don't create yet.
        // Render the distribution as a step within this same modal.
        setMultiPickerData({
          orders: ordersPayload,
          orderEntities: ordersTableData,
          pickers: selectedUsers,
        })
        setActiveTab('multi_picker')
        setBusy(false)
        return
      }

      const submitData = {
        assigned_to_id: firstPickerId,
        orders: ordersPayload,
      }
      try {
        await createPickingOrder(submitData)
      } catch (error) {
        setOrdersHaveBeenDeleted(false)
        if (error.response?.data?.error?.errors?.length > 0) {
          setReturnedOrderErrors(error.response.data.error.errors)
        } else if (error.response?.data?.error?.message) {
          window.alert(error.response?.data?.error?.message)
          console.log(error.response?.data?.error?.message)
        } else {
          window.alert(error.response?.error)
          console.log(error.response?.error)
        }
        setBusy(false)
        return
      }
    } else {
      if (selectedUsers.length === 0) {
        window.alert('missing data')
        setBusy(false)
        return
      }
      const submitData = { assigned_to_id: selectedUsers[0].id, picking_order_id: orders.map(x => x.picking_order.id) }
      try {
        await reassignPickingOrder(submitData)
      } catch (error) {
        setOrdersHaveBeenDeleted(false)
        if (error.response?.data?.error?.errors?.length > 0) {
          setReturnedOrderErrors(error.response.data.error.errors)
        } else if (error.response?.data?.error) {
          window.alert(error.response?.data?.error)
          console.log(error.response?.data?.error)
        } else {
          window.alert(error.response?.error)
          console.log(error.response?.error)
        }
        setBusy(false)
        return
      }
    }
    setBusy(false)
    onSuccess()
  }

  const [returnedOrderErrors, setReturnedOrderErrors] = useState([])

  // update order table data with errors returned by api
  useEffect(() => {
    const newOrdersTableData = ordersTableData.map(order => {
      const orderErrors = returnedOrderErrors.filter(error => error.order.id === order.id)
      return {
        ...order,
        errors: orderErrors,
      }
    })
    // console.log("newOrdersTableData: ", newOrdersTableData)
    setOrdersTableData(newOrdersTableData)
  }, [returnedOrderErrors])

  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedUserKeys, setSelectedUserKeys] = useState([])

  const totalPieces = useMemo(() => assignableOrders.reduce((acc, o) => acc + (o.entity?.assigned_stock_quantity || 0), 0), [assignableOrders])

  const groupableCount = useMemo(() => assignableOrders.filter(o => o.entity?.apt_for_single_item_picking).length, [assignableOrders])
  const individualCount = assignableOrders.length - groupableCount

  const blockedOrders = useMemo(() => (changingPicker ? [] : orders.filter(o => isBlockedFromPicking(o.entity))), [orders, changingPicker])
  const allBlocked = blockedOrders.length > 0 && blockedOrders.length === orders.length
  const buildToBreakHref = orderList => {
    const base = PICKING_TASKS_TO_BREAK.replace(':warehouse_id', warehouseId)
    const nums = orderList.map(o => o.entity?.order_number).filter(Boolean)
    return nums.length ? `${base}?pivot=order&order_numbers=${encodeURIComponent(nums.join(','))}` : base
  }
  const excludedPickabilityTotals = useMemo(
    () =>
      blockedOrders.reduce(
        (acc, o) => {
          const p = o.entity?.pickability
          acc.pickable_units += p?.pickable_units || 0
          acc.blocked_units += p?.blocked_units || 0
          acc.missing_units += p?.missing_units || 0
          return acc
        },
        { pickable_units: 0, blocked_units: 0, missing_units: 0 },
      ),
    [blockedOrders],
  )

  const orderTypes = useMemo(() => {
    const present = new Set()
    assignableOrders.forEach(o => {
      const e = o.entity || {}
      if (isRemEntity(e)) present.add('REM')
      else if (e.is_b2b) present.add('B2B')
      else present.add('D2C')
    })
    return ['D2C', 'B2B', 'REM'].filter(t => present.has(t))
  }, [assignableOrders])

  const routeAreas = useMemo(() => {
    const areas = new Set()
    assignableOrders.forEach(o => {
      const wa = o.entity?.warehouse_areas
      if (Array.isArray(wa)) wa.forEach(a => a && areas.add(String(a).trim()))
      else if (typeof wa === 'string')
        wa.split(',').forEach(a => {
          const t = a.trim()
          if (t) areas.add(t)
        })
    })
    return Array.from(areas).join(', ')
  }, [assignableOrders])

  const packBefore = useMemo(() => {
    const times = assignableOrders
      .map(o => o.entity?.max_packing_time)
      .filter(Boolean)
      .map(t => new Date(t))
    if (times.length === 0) return null
    return new Date(Math.min(...times))
  }, [assignableOrders])
  // Recomendación: máximo 2 pickers cada 100 piezas (1 picker por cada 50 piezas)
  const maxRecommendedPickers = Math.max(1, Math.floor(totalPieces / 50))
  const showPickerWarning = multiPickerEnabled && selectedUsers.length > 1 && selectedUsers.length > maxRecommendedPickers
  const [multiPickerData, setMultiPickerData] = useState(null)

  const [activeTab, setActiveTab] = useState('select_picker')
  const [pickersDetailOpen, setPickersDetailOpen] = useState(true)
  const [showOnlyGroupable, setShowOnlyGroupable] = useState(false)

  const SummaryRow = ({ label, value }) => (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-gray-600">{label}</span>
      <span className="text-xs text-gray-800 text-right">{value}</span>
    </div>
  )

  const [ordersTableData, setOrdersTableData] = useState([])
  const [targetKeys, setTargetKeys] = useState([])
  const [orderToDelete, setOrderToDelete] = useState(null)
  const [ordersTableSelectedKeys, setOrdersTableSelectedKeys] = useState([])
  const [ordersHaveBeenDeleted, setOrdersHaveBeenDeleted] = useState(false)

  const step2GroupableCount = ordersTableData.filter(o => o.apt_for_single_item_picking).length
  const ordersByKey = useMemo(() => Object.fromEntries(ordersTableData.map(o => [o.key, o])), [ordersTableData])
  const selectedSipOrders = useMemo(() => targetKeys.map(key => ordersByKey[key]).filter(Boolean), [targetKeys, ordersByKey])
  const sipBlockerOf = record => (targetKeys.includes(record.key) ? null : sipToteBlocker(record, selectedSipOrders))
  const step2DataSource = (showOnlyGroupable ? ordersTableData.filter(o => o.apt_for_single_item_picking) : ordersTableData)
    .slice()
    .sort((a, b) => (b.apt_for_single_item_picking ? 1 : 0) - (a.apt_for_single_item_picking ? 1 : 0))

  const mapOrdersAsTableData = (orders = []) => {
    const ordersTableData = []
    orders.forEach(order => {
      const foundIndex = ordersTableData.findIndex(orderTableData => orderTableData.id === order.entity.id)
      if (foundIndex === -1) {
        ordersTableData.push({
          ...order.entity,
          key: order.entity.id.toString(),
          disabled: !order.entity.apt_for_single_item_picking,
          deleteCallback: orderId => {
            setOrderToDelete(orderId)
          },
        })
      }
    })
    return ordersTableData
  }

  // delete order from table data
  useEffect(() => {
    if (orderToDelete !== null) {
      const newOrdersTableData = ordersTableData.filter(order => order.id != orderToDelete)
      // console.log("newOrdersTableData: ", newOrdersTableData)
      setOrdersTableData(newOrdersTableData)
      // unselect if selected
      setOrdersTableSelectedKeys(ordersTableSelectedKeys.filter(key => key !== orderToDelete.toString()))
      setOrderToDelete(null)
      setOrdersHaveBeenDeleted(true)
      // console.log("ordersTableSelectedKeys", ordersTableSelectedKeys)
    }
  }, [orderToDelete])

  const onPickerPageChange = page => {
    setSearchParams({ ...searchParams, page: page })
  }

  const sipTabDisabled = () => {
    return selectedUsers.length === 0 || allBlocked
  }

  const getClassForRow = record => {
    if (orderHasVisibleErrors(record)) {
      return 'bg-red-200 text-red-500'
    }
    if (!record.apt_for_single_item_picking || sipBlockerOf(record)) {
      return 'bg-gray-50 text-gray-400'
    }
    return ''
  }

  const orderHasVisibleErrors = order => {
    let errorIsVisible = false
    if (order.errors?.length > 0) {
      const direction = targetKeys.includes(order.id.toString()) ? 'right' : 'left'
      order.errors?.forEach(error => {
        if (showErrorForColumnDirection(error.error_type, direction)) {
          errorIsVisible = true
        }
      })
    }
    return errorIsVisible
  }

  const getOrdersWithVisibleErrors = (direction = null) => {
    return ordersTableData.filter(order => {
      const orderDirection = targetKeys.includes(order.id.toString()) ? 'right' : 'left'
      if (orderHasVisibleErrors(order) && (direction === null || direction === orderDirection)) {
        return true
      }
      return false
    })
  }

  return (
    <>
      <Transition show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          onClose={() => {
            // Picker-selection flow stays sticky; only the blocked state dismisses on outside click.
            if (allBlocked) onClickCancel()
          }}
        >
          <div className="flex items-center justify-center min-h-screen">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative bg-white rounded-lg shadow-xl sm:max-w-6xl sm:w-full p-6 text-gray-600 my-3 flex flex-col max-h-90vh">
                <Loader show={busy} />
                <div
                  className="absolute top-0 right-0 p-5 cursor-pointer"
                  onClick={() => {
                    onClickCancel()
                  }}
                >
                  <XIcon className="h-5 w-5" />
                </div>

                {activeTab === 'select_picker' ? (
                  allBlocked ? (
                    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
                      <ExclamationIcon className="h-12 w-12 text-red-400" />
                      <h2 className="mt-4 text-xl font-bold text-gray-800">{i18n.t('picking_tasks.assign_summary.blocked_title')}</h2>
                      <p className="mt-2 max-w-md text-gray-500">{i18n.t('picking_tasks.assign_summary.all_blocked_from_picking')}</p>
                      <div className="mt-6">
                        <Link
                          to={buildToBreakHref(blockedOrders)}
                          className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700"
                        >
                          {i18n.t('picking_tasks.assign_summary.go_to_break')}
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="pr-8">
                        <Dialog.Title className="text-2xl font-bold text-gray-800">{i18n.t('picking_tasks.assign_dialog.title')}</Dialog.Title>
                        <p className="mt-1 text-gray-500">
                          <Trans
                            i18nKey="picking_tasks.assign_dialog.intro"
                            count={assignableOrders.length}
                            components={{ bold: <span className="font-semibold text-gray-700" /> }}
                          />
                        </p>
                      </div>

                      <div className="mt-3 flex gap-6 flex-1 min-h-0">
                        <div className="flex-1 flex flex-col min-h-0">
                          <SearchInput
                            onChange={v => {
                              onSearchHandler(v)
                            }}
                            debounce={250}
                            placeholder={i18n.t('picking_tasks.assign_dialog.search_placeholder')}
                            className="focus-within:text-gray-600 text-gray-400"
                          />
                          {selectedUsers.length > 0 && (
                            <div className="mt-3 border border-blue-400 bg-blue-50 rounded-lg px-4 py-3 flex items-center justify-between">
                              <span className="text-gray-800">{i18n.t('picking_tasks.assign_dialog.selected_pickers', { count: selectedUsers.length })}</span>
                              <button
                                type="button"
                                className="text-blue-600 font-medium hover:text-blue-700"
                                onClick={() => {
                                  resetSelectedUser()
                                }}
                              >
                                {i18n.t('picking_tasks.assign_dialog.deselect_all')}
                              </button>
                            </div>
                          )}
                          <div className="relative flex-1 min-h-0 overflow-y-auto mt-3">
                            <Loader show={isFetching || isLoading} />
                            <Table
                              rowSelection={{
                                type: multiPickerEnabled ? 'checkbox' : 'radio',
                                preserveSelectedRowKeys: true,
                                onChange: (selectedRowKeys, selectedRows) => {
                                  setSelectedUsers(selectedRows)
                                  setSelectedUserKeys(selectedRowKeys)
                                },
                                selectedRowKeys: selectedUserKeys,
                              }}
                              size="small"
                              columns={pickerColumns}
                              dataSource={pickerData}
                              pagination={false}
                              onRow={(record, rowIndex) => {
                                return {
                                  onClick: event => {
                                    const recordKey = record.id.toString()
                                    if (!multiPickerEnabled) {
                                      setSelectedUsers([record])
                                      setSelectedUserKeys([recordKey])
                                      return
                                    }
                                    setSelectedUsers(prev =>
                                      prev.some(u => u.id.toString() === recordKey) ? prev.filter(u => u.id.toString() !== recordKey) : [...prev, record],
                                    )
                                    setSelectedUserKeys(prev => (prev.includes(recordKey) ? prev.filter(k => k !== recordKey) : [...prev, recordKey]))
                                  },
                                }
                              }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-sm text-gray-500">
                              {i18n.t('picking_tasks.assign_dialog.operators_count', { n: pickerRawData?.meta?.total_count ?? '' })}
                            </span>
                            <Pagination
                              size="small"
                              total={pickerRawData?.meta?.total_count}
                              current={searchParams.page}
                              onChange={page => onPickerPageChange(page)}
                              showSizeChanger={false}
                            />
                          </div>
                        </div>

                        <div className="w-72 flex-shrink-0 min-h-0 flex flex-col">
                          <h3 className="text-base font-bold text-gray-800 mb-3">{i18n.t('picking_tasks.assign_dialog.summary_title')}</h3>

                          <div className="mb-3">
                            <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 flex items-center justify-between">
                              <span className="font-medium text-gray-700">{i18n.t('picking_tasks.assign_dialog.orders_to_assign')}</span>
                              <span className="font-medium text-gray-700">{assignableOrders.length}</span>
                            </div>
                            <div className="px-4 pt-3 space-y-2">
                              <SummaryRow label={i18n.t('picking_tasks.assign_dialog.individual')} value={individualCount} />
                              <SummaryRow label={i18n.t('picking_tasks.assign_dialog.groupable')} value={groupableCount} />
                              <SummaryRow label={i18n.t('picking_tasks.assign_dialog.total_pieces')} value={totalPieces} />
                              {routeAreas && <SummaryRow label={i18n.t('picking_tasks.assign_dialog.route_areas')} value={routeAreas} />}
                              {orderTypes.length > 0 && (
                                <div className="flex items-start justify-between gap-2">
                                  <span className="text-xs text-gray-600">{i18n.t('picking_tasks.assign_dialog.order_type')}</span>
                                  <span className="flex flex-wrap gap-1 justify-end">
                                    {orderTypes.map(t => (
                                      <span
                                        key={t}
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${ORDER_TYPE_PILL[t] || 'bg-gray-100 text-gray-700'}`}
                                      >
                                        {t}
                                      </span>
                                    ))}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 flex items-center justify-between gap-2 mb-3">
                            <span className="font-medium text-gray-700">{i18n.t('list_orders.headers.max_packing_time')}</span>
                            {packBefore ? (
                              <FormattedRelativeTime date={packBefore} calendarTime className="font-medium text-gray-700 text-right" />
                            ) : (
                              <span className="font-medium text-gray-700">-</span>
                            )}
                          </div>

                          <div className="flex-1 min-h-0 flex flex-col">
                            <button
                              type="button"
                              onClick={() => setPickersDetailOpen(prev => !prev)}
                              className="flex-shrink-0 w-full border border-gray-200 rounded-lg bg-gray-50 p-4 flex items-center justify-between"
                            >
                              <span className="font-medium text-gray-700">{i18n.t('picking_tasks.assign_dialog.pickers_detail')}</span>
                              <span className="flex items-center gap-2">
                                <span className="font-medium text-gray-700">{selectedUsers.length}</span>
                                {selectedUsers.length > 0 && (
                                  <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${pickersDetailOpen ? 'rotate-180' : ''}`} />
                                )}
                              </span>
                            </button>
                            {pickersDetailOpen && selectedUsers.length > 0 && (
                              <div className="px-4 pt-3 space-y-3 overflow-y-auto min-h-0">
                                {selectedUsers.map(u => {
                                  const queued = (u.new_picking_orders || []).length
                                  return (
                                    <div key={u.id}>
                                      <div className="text-xs text-gray-700 break-all">{u.email || `${u.first_name} ${u.last_name}`}</div>
                                      {queued > 0 && (
                                        <div className="text-xs text-gray-400">{i18n.t('picking_tasks.assign_dialog.queued_orders', { count: queued })}</div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-gray-100">
                        {showPickerWarning && (
                          <div className="mb-3 p-2.5 bg-yellow-50 border border-yellow-300 rounded-md text-xs text-yellow-800 flex items-start gap-2">
                            <ExclamationIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-600" />
                            <span>
                              {i18n.t('picking_tasks.assign_summary.too_many_pickers_warning', {
                                pieces: totalPieces,
                                selected: selectedUsers.length,
                                recommended: maxRecommendedPickers,
                              })}
                            </span>
                          </div>
                        )}
                        {blockedOrders.length > 0 && (
                          <div className="mb-3 p-3 bg-red-50 border-2 border-red-400 rounded-md text-sm text-red-900 flex items-start gap-2">
                            <ExclamationIcon className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-500" />
                            <div>
                              <div>
                                <span className="font-bold">{i18n.t('picking_tasks.assign_summary.excluded_title')}: </span>
                                {i18n.t('picking_tasks.assign_summary.excluded_blocked_from_picking')}
                              </div>
                              <OrderNumberList orders={blockedOrders} />
                              <Link to={buildToBreakHref(blockedOrders)} className="inline-block mt-1 font-semibold underline">
                                {i18n.t('picking_tasks.assign_summary.go_to_break')}
                              </Link>
                              <PickabilityBreakdown pickability={excludedPickabilityTotals} showPickable={false} showMissing={false} className="mt-1" />
                            </div>
                          </div>
                        )}
                        <PickingOrderErrorsPanel errors={returnedOrderErrors} className="mb-3" scrollable />
                        <div className="flex gap-3">
                          <Button
                            className="flex-1"
                            type="secondary"
                            onClick={() => {
                              onClickCancel()
                            }}
                          >
                            {i18n.t('picking_tasks.multi_picker.cancel')}
                          </Button>
                          <Button
                            className="flex-1"
                            disabled={sipTabDisabled()}
                            type="primary"
                            loading={busy && selectedUsers.length <= 1 && groupableCount < 2}
                            loading_label={i18n.t('picking_tasks.assign_dialog.loading')}
                            onClick={() => {
                              if (groupableCount >= 2) {
                                setActiveTab('select_sips')
                              } else {
                                onSubmit()
                              }
                            }}
                          >
                            {selectedUsers.length === 0
                              ? multiPickerEnabled
                                ? i18n.t('picking_tasks.multi_picker.no_pickers_selected')
                                : i18n.t('picking_tasks.assign_dialog.choose_operator')
                              : i18n.t('picking_tasks.assign_dialog.continue')}
                          </Button>
                        </div>
                      </div>
                    </>
                  )
                ) : activeTab === 'multi_picker' ? (
                  <MultiPickerAssignView
                    open
                    orders={multiPickerData?.orders || []}
                    pickers={multiPickerData?.pickers || []}
                    onBack={() => setActiveTab(groupableCount >= 2 ? 'select_sips' : 'select_picker')}
                    onSuccess={onSuccess}
                  />
                ) : (
                  <>
                    <div className="pr-8">
                      <Dialog.Title className="text-2xl font-bold text-gray-800">{i18n.t('picking_tasks.assign_dialog.group_title')}</Dialog.Title>
                      <p className="mt-1 text-gray-500">{i18n.t('picking_tasks.assign_dialog.group_help')}</p>
                    </div>

                    <div className="mt-4 flex-shrink-0">
                      <h4 className="text-base font-semibold text-gray-800">{i18n.t('picking_tasks.assign_dialog.group_orders_heading')}</h4>
                      <p className="mt-0.5 text-sm text-gray-500">
                        <Trans
                          i18nKey="picking_tasks.assign_dialog.group_eligible"
                          values={{ total: ordersTableData.length, eligible: step2GroupableCount }}
                          components={{ bold: <span className="font-semibold text-gray-700" /> }}
                        />
                      </p>
                    </div>

                    <div className="mt-3 flex-shrink-0 border border-blue-400 bg-blue-50 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                      <span className="text-gray-800">
                        <Trans
                          i18nKey="picking_tasks.assign_dialog.shared_tote"
                          values={{ n: targetKeys.length }}
                          components={{ bold: <span className="font-semibold" /> }}
                        />
                      </span>
                      <button
                        type="button"
                        className="text-blue-600 font-medium hover:text-blue-700 whitespace-nowrap"
                        onClick={() => setShowOnlyGroupable(prev => !prev)}
                      >
                        {showOnlyGroupable ? i18n.t('picking_tasks.assign_dialog.show_all') : i18n.t('picking_tasks.assign_dialog.show_only_groupable')}
                      </button>
                    </div>

                    <div className="mt-3 flex-1 min-h-0 overflow-y-auto">
                      <Table
                        rowSelection={{
                          type: 'checkbox',
                          selectedRowKeys: targetKeys,
                          getCheckboxProps: record => ({ disabled: !record.apt_for_single_item_picking || sipBlockerOf(record) !== null }),
                          onChange: selectedRowKeys => {
                            setTargetKeys(keepCompatibleSipKeys(selectedRowKeys, ordersByKey))
                          },
                          renderCell: (checked, record, index, originNode) => {
                            if (!record.apt_for_single_item_picking) {
                              return (
                                <Tooltip title={i18n.t('picking_tasks.assign_dialog.not_groupable_tooltip')}>
                                  <span className="inline-flex cursor-not-allowed">{originNode}</span>
                                </Tooltip>
                              )
                            }
                            const blocker = sipBlockerOf(record)
                            if (blocker) {
                              return (
                                <Tooltip
                                  title={i18n.t('picking_tasks.assign_dialog.insert_blocker_tooltip', {
                                    orderNumber: blocker.order_number,
                                    store: blocker.store?.name || i18n.t('picking_tasks.assign_dialog.other_store'),
                                  })}
                                >
                                  <span className="inline-flex cursor-not-allowed">{originNode}</span>
                                </Tooltip>
                              )
                            }
                            return originNode
                          },
                        }}
                        size="small"
                        columns={groupOrdersColumns(step2GroupableCount)}
                        dataSource={step2DataSource}
                        pagination={false}
                        rowClassName={record => getClassForRow(record)}
                      />
                      {getOrdersWithVisibleErrors().length > 0 && (
                        <div className="mt-2 text-red-500 text-right">
                          <span>{i18n.t('picking_tasks.assign_dialog.resolve_conflicts', { count: getOrdersWithVisibleErrors().length })}</span>
                        </div>
                      )}
                      <PickingOrderErrorsPanel errors={returnedOrderErrors} className="mt-2" scrollable />
                    </div>
                    <div className="mt-5 pt-4 border-t border-gray-100 flex gap-3">
                      <Button
                        className="flex-1"
                        type="secondary"
                        onClick={() => {
                          setActiveTab('select_picker')
                        }}
                      >
                        {i18n.t('picking_tasks.assign_dialog.back')}
                      </Button>
                      <Button
                        className="flex-1"
                        disabled={getOrdersWithVisibleErrors().length > 0 && !ordersHaveBeenDeleted}
                        type="primary"
                        onClick={() => {
                          onSubmit()
                        }}
                        loading={busy && selectedUsers.length <= 1}
                        loading_label={i18n.t('picking_tasks.assign_dialog.loading')}
                      >
                        {selectedUsers.length > 1
                          ? i18n.t('picking_tasks.assign_dialog.continue')
                          : i18n.t('picking_tasks.assign_dialog.confirm_orders', { count: ordersTableData.length })}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
